import { useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { Link, useNavigate, useParams } from "react-router";
import { useToast } from "../../components/ui/Toast";
import {
  CustomUrlEditor,
  customUrlFormValuesFromRecord,
  type CustomUrlFormValues,
} from "./custom-url-edit/CustomUrlEditor";
import {
  DELETE_CUSTOM_URL,
  UPDATE_CUSTOM_URL,
  evictCustomUrlListings,
} from "./custom-url-edit/customUrlMutations";
import { useCustomUrlData } from "./custom-url-edit/useCustomUrlData";

interface UpdateResponse {
  updateCustomUrl: { id: string; slug: string } | null;
}

export function CustomUrlEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { url, loading, error } = useCustomUrlData(id);
  const [update, updateResult] = useMutation<UpdateResponse>(
    UPDATE_CUSTOM_URL,
    {
      refetchQueries: ["AllCustomUrlsForListing"],
      update: evictCustomUrlListings,
    },
  );
  const [del, delResult] = useMutation(DELETE_CUSTOM_URL, {
    refetchQueries: ["AllCustomUrlsForListing"],
    update: evictCustomUrlListings,
  });
  const [saveError, setSaveError] = useState<string | null>(null);

  const initialValues = useMemo(
    () => (url ? customUrlFormValuesFromRecord(url) : null),
    [url],
  );

  async function handleSave(values: CustomUrlFormValues) {
    if (!id) return;
    setSaveError(null);
    try {
      const result = await update({
        variables: {
          id,
          slug: values.slug,
          targetType: values.targetType,
          targetLandingPageId: values.targetLandingPageId,
          targetPath: values.targetPath,
          isActive: values.isActive,
        },
      });
      const updated = result.data?.updateCustomUrl;
      if (!updated) throw new Error("Couldn't save custom URL");
      toast.push({
        kind: "success",
        title: `"${values.slug}" saved`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      setSaveError(message);
      toast.push({
        kind: "error",
        title: "Couldn't save URL",
        description: message,
      });
    }
  }

  async function handleDelete() {
    if (!id || !url) return;
    const ok = window.confirm(
      `Delete "${url.slug}"? This removes the custom URL permanently.`,
    );
    if (!ok) return;
    try {
      await del({ variables: { id } });
      toast.push({
        kind: "success",
        title: `"${url.slug}" deleted`,
      });
      navigate("/website/custom-urls");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      toast.push({
        kind: "error",
        title: "Couldn't delete URL",
        description: message,
      });
    }
  }

  if (loading && !url) {
    return (
      <div className="px-7 py-10 text-[13px] text-ink-4">Loading URL…</div>
    );
  }
  if (error || !url || !initialValues) {
    return (
      <div className="px-7 py-10">
        <div className="mb-3 text-[13px] text-rust">
          Couldn't load URL: {error?.message ?? "not found"}
        </div>
        <Link
          to="/website/custom-urls"
          className="text-[13px] text-tide hover:underline"
        >
          ← Back to custom URLs
        </Link>
      </div>
    );
  }

  return (
    <CustomUrlEditor
      mode="edit"
      initialValues={initialValues}
      updatedAt={url.updatedAt}
      isSaving={updateResult.loading}
      saveError={saveError}
      onSave={handleSave}
      onDelete={handleDelete}
      isDeleting={delResult.loading}
    />
  );
}
